import { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import Modal from './Modal';
import PosCategoryIconModal from './PosCategoryIconModal';
import { spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import {
  subscribePosPaymentMethods, savePosPaymentMethods, posPaymentMethodKey,
  posIconIsIconify, posIconifyUrl,
} from '../utils/posUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { POS_PAYMENT_LABEL_MAX_LENGTH, POS_PAYMENT_METHODS_MAX } from '../constants/posConst';
import '../css/Pos.css';

/**
 * Payments — how this counter takes money.
 *
 * PER CLIENT. One club takes GCash and Maya, another only cash, a third has a
 * house account. Which buttons appear at the confirm step is the club's own
 * business, so it is edited here rather than by an administrator.
 *
 * A KEY IS SET ONCE AND THEN LEFT ALONE. Every order stores the key it was
 * taken under, and Sales groups a day's takings by it — so renaming a method
 * is a label change that history follows along with, while reusing a key for
 * something else would silently re-file old sales under the new name. The key
 * is derived from the label when a row is first added and never recomputed.
 *
 * @returns {JSX.Element}
 */
function PosPaymentMethodsForm() {
  const { clientId } = useConfig();
  const { staffEmail } = usePosSession();
  const [methods, setMethods] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  // Index of the row whose icon is being picked, or null.
  const [iconFor, setIconFor] = useState(null);

  // A ROW'S IDENTITY, and deliberately not its key. An unsaved row's key
  // follows what is being typed into it, so keying the list on that made React
  // treat each keystroke as a different row: it threw the <input> away, built a
  // new one, and the caret went with it. This never changes for as long as the
  // row exists, so the same <input> survives the whole edit.
  const nextUid = useRef(0);

  /**
   * Tags a stored method with a row identity for React.
   *
   * `saved: true` is the other half: it marks the key as PERMANENT, so editing
   * the label of a method sales have already been taken under renames it
   * rather than re-filing that history under a new key.
   *
   * @param {{key: string, label: string, icon?: string}} method
   * @returns {object} the same method, with uid and saved
   */
  const asRow = (method) => {
    nextUid.current += 1;
    return { ...method, uid: `row-${nextUid.current}`, saved: Boolean(method.key) };
  };

  useEffect(() => {
    const stop = subscribePosPaymentMethods(clientId, (list) => {
      // Seeded once. Bound live, a row would be rewritten under the cursor by
      // the list it is editing.
      setMethods((current) => current ?? list.map(asRow));
    });
    return stop;
    // asRow is stable in practice (it only touches a ref) and re-running this
    // on every render would re-subscribe on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const update = (index, patch) => setMethods((list) => list.map((row, i) => {
    if (i !== index) return row;
    const next = { ...row, ...patch };
    // A row that has never been saved has no permanent key yet, so it follows
    // the label until it does.
    if (patch.label !== undefined && !row.saved) next.key = posPaymentMethodKey(patch.label);
    return next;
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const stored = await savePosPaymentMethods(clientId, methods, staffEmail);
      // Matched back by key rather than by position, because the save drops
      // blank rows — so the list coming back can be shorter than the one that
      // went in, and matching by index would hand a row someone else's uid.
      setMethods((current) => stored.map((m) => {
        const before = (current || []).find(row => row.key === m.key);
        return before
          ? { ...before, ...m, saved: true }
          : asRow(m);
      }));
      setModal({
        type: 'success',
        title: 'Payment methods saved',
        message: 'These are the options staff will see at the confirm step.',
      });
    } catch (error) {
      setModal({
        type: 'error',
        title: 'Could not save',
        message: describeWriteError(error, {
          what: 'these payment methods',
          path: `posTenants/${clientId}/paymentMethods`,
        }),
      });
    } finally {
      setSaving(false);
    }
  };

  if (methods === null) {
    return <div className="pos-loading">{spinnerRing('Loading payment methods…')}</div>;
  }

  /**
   * One method's icon as the till will draw it.
   * @param {object} row
   * @returns {JSX.Element|null}
   */
  const renderIcon = (row) => {
    if (!row.icon) return null;
    if (!posIconIsIconify(row.icon)) return <span aria-hidden="true">{row.icon}</span>;
    return (
      <span
        className="pos-icon-mask"
        style={{ WebkitMaskImage: `url(${posIconifyUrl(row.icon)})`, maskImage: `url(${posIconifyUrl(row.icon)})` }}
        aria-hidden="true"
      />
    );
  };

  return (
    <div className="pos-config">
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Payments</h2>
          <p>
            The options staff choose between when confirming a sale. Saved changes apply
            to everyone here straight away, including tills already open.
          </p>
        </div>

        <ul className="pos-paylist">
          {methods.map((row, index) => {
            // A row that has not been named yet still has to be describable:
            // "Remove " with nothing after it is what a screen reader would
            // otherwise announce on a row someone just added.
            const name = row.label.trim() || `payment method ${index + 1}`;
            return (
              <li key={row.uid} className="pos-payrow">
                {/* The icon slot doubles as the way to change it, exactly as a
                    category chip's does — there is no room for a separate
                    control and the icon is what you would aim at anyway. */}
                <button
                  type="button"
                  className={`pos-category-icon${row.icon ? ' set' : ''}`}
                  onClick={() => setIconFor(index)}
                  disabled={saving}
                  aria-label={row.icon ? `Change the icon for ${name}` : `Add an icon to ${name}`}
                  title={row.icon ? 'Change icon' : 'Add icon'}
                >
                  {renderIcon(row) || <Plus size={11} />}
                </button>

                <input
                  type="text"
                  className="pos-payrow-label"
                  value={row.label}
                  onChange={(e) => update(index, { label: e.target.value })}
                  disabled={saving}
                  maxLength={POS_PAYMENT_LABEL_MAX_LENGTH}
                  placeholder="e.g. GCash"
                  aria-label={`Name of payment method ${index + 1}`}
                />

                <button
                  type="button"
                  className="pos-payrow-remove"
                  onClick={() => setMethods(list => list.filter((_, i) => i !== index))}
                  disabled={saving || methods.length <= 1}
                  title={methods.length <= 1 ? 'Keep at least one' : `Remove ${name}`}
                  aria-label={`Remove ${name}`}
                >
                  <X size={14} />
                </button>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="pos-btn pos-btn-ghost pos-payadd"
          onClick={() => setMethods(list => [...list, asRow({ key: '', label: '', icon: '' })])}
          disabled={saving || methods.length >= POS_PAYMENT_METHODS_MAX}
        >
          <Plus size={14} /> Add payment method
        </button>

        <p className="pos-theme-hint">
          Renaming a method is safe — past sales keep the name they were taken under, so
          old receipts and reports do not change.
        </p>

        <div className="pos-theme-actions">
          <button type="button" className="pos-btn pos-btn-ghost" onClick={() => setMethods(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      </section>

      {iconFor !== null && (
        <PosCategoryIconModal
          categoryName={methods[iconFor]?.label || 'this method'}
          value={methods[iconFor]?.icon || ''}
          onSave={(icon) => { update(iconFor, { icon }); setIconFor(null); }}
          onClose={() => setIconFor(null)}
        />
      )}

      <Modal
        isOpen={Boolean(modal)}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText="OK"
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosPaymentMethodsForm;
